import { useAtom } from 'jotai';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { localDataService } from '@/providers/rxdb/local-data-service';

import { appRoutes } from '@/constants/routes';
import type { LogoutRouteState } from '@/features/logout';
import { showSyncModalAtom, syncProgressAtom } from '@/state';

import { getNextFakeProgress } from '@/utils/helper-utils';

const SYNC_TIMEOUT_MS = 750;

// The logout in progress, shared by every instance of the hook: the menu starts the logout, but the
// modal (SaveBeforeLogoutModal calls this hook itself) is what cancels it or discards the changes.
let activeSyncWait: AbortController | null = null;

export const useSafeLogout = () => {
  const [showSyncModal, setShowSyncModal] = useAtom(showSyncModalAtom);
  const [syncProgress, setSyncProgress] = useAtom(syncProgressAtom);

  const navigate = useNavigate();

  useEffect(() => {
    !showSyncModal && setSyncProgress(0);

    let interval: ReturnType<typeof setInterval> | undefined;

    if (showSyncModal) {
      interval = setInterval(() => {
        setSyncProgress(getNextFakeProgress);
      }, 300);
    }

    return () => {
      interval && clearInterval(interval);
    };
  }, [showSyncModal]);

  const logout = async () => {
    // A logout already in progress owns the one wait (e.g. a double click before the modal covers
    // the menu): a second wait would orphan the first, which cancel could then no longer stop.
    if (activeSyncWait && !activeSyncWait.signal.aborted) {
      return;
    }

    // One wait per logout, shared by the quick check and the modal: each wait polls the leader tab
    // for a count until it is aborted (cancel, lose data) or everything is pushed.
    const syncWait = new AbortController();
    activeSyncWait = syncWait;
    let inSync = localDataService.awaitInSync(syncWait.signal);

    try {
      // logout immediately if sync finishes within SYNC_TIMEOUT_MS
      await Promise.race([inSync, new Promise((_, reject) => setTimeout(() => reject('long-sync'), SYNC_TIMEOUT_MS))]);
      finishSyncWait(syncWait);
      navigate(appRoutes.logout);
    } catch (error) {
      // show modal and wait for sync
      if (error !== 'long-sync') {
        console.warn('Sync error during logout:', error);
        // That wait has ended with the error, so the modal needs a fresh one.
        inSync = localDataService.awaitInSync(syncWait.signal);
      }

      setShowSyncModal(true);

      await Promise.race([inSync, new Promise((resolve) => syncWait.signal.addEventListener('abort', resolve, { once: true }))]);

      // Aborted: cancelLogout or forceLogout has already handled it.
      if (syncWait.signal.aborted) {
        return;
      }

      setSyncProgress(100);
      // pause to let the user see the progress reach 100% before closing the modal
      await new Promise((resolve) => setTimeout(resolve, 300));
      setShowSyncModal(false);
      finishSyncWait(syncWait);
      navigate(appRoutes.logout);
    }
  };

  const cancelLogout = () => {
    activeSyncWait?.abort();
    setShowSyncModal(false);
    setSyncProgress(0);
  };

  const forceLogout = () => {
    activeSyncWait?.abort();
    setShowSyncModal(false);
    navigate(appRoutes.logout, { state: { discardConfirmed: true } satisfies LogoutRouteState });
  };

  return {
    logout,
    forceLogout,
    cancelLogout,
    syncProgress,
    showSyncModal,
  };
};

// Releases the logout in progress once it has handed over to the logout route.
const finishSyncWait = (syncWait: AbortController) => {
  if (activeSyncWait === syncWait) {
    activeSyncWait = null;
  }
};
